import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AdminLayout.css';
import {
  BoxIcon,
  ClipboardIcon,
  FileTextIcon,
  Gamepad,
  LayersIcon,
  LucideLayoutDashboard,
  UserIcon,
} from 'lucide-react';
import vangleImg from '../../Assets/vangle.jpeg';
import { OrderedListOutlined, ShopFilled } from '@ant-design/icons';
import { VideoIcon, ListVideoIcon, CreditCardIcon } from 'lucide-react';

const AdminLayout = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Update mobile state on window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => setSidebarVisible(!sidebarVisible);

  // Mapping of pathnames to page titles
  const pageTitles = {
    '/': 'Dashboard',
    '/user': 'Manage Users',
    '/item': 'Manage Items',
    '/task': 'Manage Tasks',
    '/category': 'Manage Categories',
    '/order': 'Manage User Orders',
    '/usertask': 'Manage User Tasks',
    '/sosuser': 'Manage User SOS',
    '/users': 'User Transactions',
  };

  const currentTitle = pageTitles[location.pathname] || 'Dashboard';

  const NavLink = ({ to, icon, text, tooltip }) => (
    <li className={location.pathname === to ? 'active' : ''}>
      <Link to={to} data-tooltip={tooltip} onClick={() => isMobile && setSidebarVisible(false)}>
        <span className="nav-icon">{icon}</span>
        {!sidebarCollapsed && <span className="nav-text">{text}</span>}
      </Link>
    </li>
  );

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${sidebarVisible ? 'show' : ''}`}
      >
        <div className="sidebar-header">
          {!isMobile && <h2 className="logo">Side Menu</h2>}
          {!isMobile && (
            <button
              className="collapse-btn"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? '→' : '←'}
            </button>
          )}
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            <img src={vangleImg} alt="User Avatar" />
          </div>
          {!sidebarCollapsed && (
            <div className="user-info">
              <h3>{currentUser?.name || 'Admin'}</h3>
              <p>{currentUser?.isSuperAdmin ? 'Super Admin' : 'Admin'}</p>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            {!sidebarCollapsed && <h4 className="nav-title">MAIN</h4>}
            <ul className="nav-links">
              <NavLink
                to="/"
                icon={<LucideLayoutDashboard />}
                text="Dashboard"
                tooltip="Dashboard"
              />
            </ul>
          </div>

          <div className="nav-section">
            {!sidebarCollapsed && <h4 className="nav-title">MANAGE MEMBERS AND ORDERS</h4>}
            <ul className="nav-links">
              <NavLink to="/user" icon={<UserIcon />} text="Manage User" tooltip="Manage User" />
              <NavLink to="/item" icon={<BoxIcon />} text="Manage Item" tooltip="Manage Item" />
              <NavLink
                to="/task"
                icon={<ClipboardIcon />}
                text="Manage Task"
                tooltip="Manage Task"
              />
            </ul>
          </div>

          <div className="nav-section">
            {!sidebarCollapsed && <h4 className="nav-title">MANAGE MASTER</h4>}
            <ul className="nav-links">
              <NavLink
                to="/category"
                icon={<LayersIcon />}
                text="Category Tasks"
                tooltip="Category Tasks "
              />
               <NavLink
                to="/category-items"
                icon={<LayersIcon />}
                text="Category Items"
                tooltip="Category Items"
              />
              <NavLink
                to="/order"
                icon={<FileTextIcon />}
                text="Manage User Orders"
                tooltip="User Orders"
              />
              <NavLink
                to="/usertask"
                icon={<UserIcon />}
                text="Manage User Task"
                tooltip="User Task"
              />
              <NavLink
                to="/sosuser"
                icon={<UserIcon />}
                text="Manage User SOS"
                tooltip="User SOS"
              />
              <NavLink
                to="/users"
                icon={<FileTextIcon />}
                text="User Transactions"
                tooltip="User Transactions"
              />
               <NavLink
                to="/vendor"
                icon={<ShopFilled />}
                text="Vendor Mangement"
                tooltip="Vendor Mangement"
              />
                 <NavLink
                to="/spin"
                icon={<Gamepad />}
                text="Spin Management"
                tooltip="Spin Management"
              />
               <NavLink
                to="/vendororder"
                icon={<OrderedListOutlined />}
                text="Vendor order"
                tooltip="Vendor order"
              />
                 <NavLink
                to="/admincategory"
                icon={<VideoIcon />}
                text="Video Categories"
                tooltip="Video Categories"
              />
              <NavLink
                to="/adminvideos"
                icon={<ListVideoIcon />}
                text="Video Manager"
                tooltip="Video Manager"
              />
              <NavLink
                to="/adminplans"
                icon={<CreditCardIcon />}
                text="Video Plans"
                tooltip="Video Plans"
              />

            </ul>
          </div>
        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn">
            <i className="fas fa-sign-out-alt"></i>
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Backdrop */}
      {isMobile && (
        <div
          className={`sidebar-backdrop ${sidebarVisible ? 'show' : ''}`}
          onClick={toggleSidebar}
        />
      )}

      {/* Main Content */}
      <main className="main-content">
        <header className="top-header">
          {/* Mobile Menu Button */}
          {isMobile && (
            <button className="menu-btn" onClick={toggleSidebar}>
              ☰
            </button>
          )}

          <div className="header-title">
            <h1>{currentTitle}</h1>
            <nav className="breadcrumb">
              <span className="breadcrumb-item">Home</span>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-item active">{currentTitle}</span>
            </nav>
          </div>

          <div className="header-actions">
            <div className="header-greeting">
              Hello, {currentUser?.name || 'Admin'} (
              {currentUser?.isSuperAdmin ? 'Super Admin' : 'Admin'})
            </div>
            <button className="btn-icon" onClick={handleLogout}>
              <i className="fas fa-power-off"></i>
            </button>
          </div>
        </header>

        <div className="content-wrapper">
          <Outlet />
        </div>

        <footer className="main-footer">
          <p>2025 © Created with ❤️ by AP IT SERVICES</p>
        </footer>
      </main>
    </div>
  );
};

export default AdminLayout;